import { useEffect, useRef, useState } from "react";

import Spinner from "../../../shared/ui/Loading/Spinner";
import Text from "../../../shared/ui/Typography/Text";
import PublicPresentationRenderer from "../../clientPresentation/widgets/PublicPresentationRenderer";
import {
  fetchClientPresentations,
  unwrapPresentationList,
} from "../../clientPresentation/api/clientPresentations";
import { pickPublicProjection } from "../../clientPresentation/lib/publicProjection";
import {
  STATUS_LABELS,
  formatDate,
  formatDateTime,
  presentationStatus,
} from "../../clientPresentation/lib/labels";

function ClientPresentationsCard({ client, clientId }) {
  const [items, setItems] = useState([]);
  const [loadState, setLoadState] = useState("idle");
  const [loadError, setLoadError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadedClientId, setLoadedClientId] = useState(null);
  const requestRef = useRef(0);

  useEffect(() => {
    if (clientId == null) {
      setItems([]);
      setPreview(null);
      setLoadError(null);
      setLoadState("idle");
      setLoadedClientId(null);
      return undefined;
    }
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const controller = new AbortController();
    setItems([]);
    setPreview(null);
    setLoadError(null);
    setLoadedClientId(null);
    setLoadState("loading");
    fetchClientPresentations(clientId, { signal: controller.signal })
      .then((data) => {
        if (requestRef.current !== requestId) return;
        setItems(unwrapPresentationList(data));
        setLoadedClientId(clientId);
        setLoadState("loaded");
      })
      .catch((error) => {
        if (requestRef.current !== requestId) return;
        if (error && error.code === "ERR_CANCELED") return;
        setLoadError("Не вдалося завантажити презентації.");
        setLoadState("error");
      });
    return () => controller.abort();
  }, [clientId]);

  if (!client) return null;

  const visibleItems =
    loadState === "loaded" && loadedClientId === clientId ? items : [];

  return (
    <article className="cd-card" aria-labelledby="client-presentations-heading" data-testid="client-presentations-card">
      <header className="cd-card__head">
        <h3 id="client-presentations-heading">
          <i className="fa-solid fa-file-lines" aria-hidden="true" />
          <span>Презентації</span>
        </h3>
      </header>
      {loadState === "loading" ? <Spinner size="sm" label="Завантаження презентацій" /> : null}
      {loadState === "error" ? (
        <Text as="p" size="sm" tone="danger">
          {loadError}
        </Text>
      ) : null}
      {loadState === "loaded" && visibleItems.length === 0 ? (
        <Text as="p" size="sm" tone="secondary">
          Немає презентацій, прив'язаних до цього клієнта.
        </Text>
      ) : null}
      {visibleItems.length ? (
        <ul className="presentation-client-list">
          {visibleItems.map((share) => {
            const status = presentationStatus(share);
            return (
              <li key={share.id}>
                <strong>{share.title || "Об'єкт"}</strong>
                <span>{share.internal_label || "Без внутрішньої назви"}</span>
                <span>
                  {STATUS_LABELS[status]} · до {formatDate(share.expires_at) || "—"}
                </span>
                <span>
                  Перегляди: {share.unique_views ?? 0}
                  {share.last_viewed_at ? ` · ${formatDateTime(share.last_viewed_at)}` : ""}
                </span>
                <button type="button" className="action-btn" onClick={() => setPreview(share)}>
                  Перегляд
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {preview && loadedClientId === clientId ? (
        <div className="presentation-modal" role="dialog" aria-modal="true">
          <div className="presentation-modal__backdrop" onClick={() => setPreview(null)} />
          <div className="presentation-modal__dialog">
            <header className="presentation-modal__head">
              <h2>Перегляд презентації</h2>
              <button type="button" className="presentation-modal__close" onClick={() => setPreview(null)} aria-label="Закрити">
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </header>
            <div className="presentation-modal__body">
              <PublicPresentationRenderer presentation={pickPublicProjection(preview)} footer={false} />
            </div>
          </div>
        </div>
      ) : null}
    </article>
  );
}

export default ClientPresentationsCard;
